import axios, { AxiosResponse } from "axios";

import { HW_API_URL } from "../../configs";

import { QuoteCalculateParams, QuoteCalculateResponse } from "./types";

/**
 * Node (hunkware-api) public quote-calculate client for booking-v4, used on the
 * `HunkWare_Node_Version_Calculate_Quote` branch. The PHP path stays on the
 * shared axios `fetcher` untouched; this is the drop-in alternative that talks to
 * the new Node engine (`@hunkware-api/quotes`).
 *
 * Engine: the PHP path sends `newBooking`/`trimResponse`, which the fetcher
 * snake_cases to `new_booking`/`trim_response`. Bare axios does no casing, so
 * the snake_case names are written here — the Node controller reads them from
 * the raw query. Without `new_booking` the route runs the LEGACY engine, whose
 * snake_case body has no `onSiteEstimateRequired`/`callRequired`/`hunks` for
 * `use-contract-executors.ts` to store, and the OSE branching falls through.
 *
 * Response: the new-booking engine answers camelCase already — the same keys
 * the fetcher's response camelCase produces from PHP — so it is returned raw.
 *
 * Casing: the request payload is camelCase (as built by the consumer), and the
 * Node route camelCases the body itself, so it is sent verbatim.
 *
 * Auth: the public Node endpoint is token-guarded (`?token=<64 hex>` + `accountId`
 * path match), not JWT — the token is passed as a query param exactly as on the PHP
 * path. No Bearer/refresh dance.
 *
 * Errors: the workflow-engine runner reads only `error.response?.data?.meta?.series`,
 * fully optional-chained — a meta-less NestJS error body leaves `series`
 * undefined and the alert falls back to its generic message. The raw AxiosError
 * is re-thrown as-is.
 */
export const calculateQuoteViaNode = ({
  locationId,
  accountId,
  payload,
  token,
}: QuoteCalculateParams): Promise<AxiosResponse<QuoteCalculateResponse>> =>
  axios.post<QuoteCalculateResponse>(
    `${HW_API_URL}/public/website/locations/${locationId}/accounts/${accountId}/quotes/calculate`,
    payload,
    {
      params: { token, new_booking: true, trim_response: true },
    },
  );
